/* 
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved. 
* Distributed under the TruVideo proprietary license. 
*/
import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Building2,
  Check,
  ChevronDown,
  ChevronRight,
  Copy,
  Info,
  Layers,
  Palette,
  X,
} from 'lucide-react';
import { accountService } from '../services/accountService';
import { subAccountService } from '../services/subAccountService';
import { useAuth } from '../context/AuthContext';
import toastService from '../utils/toastService';
import { DAISY_THEMES } from '../utils/constants';

const ROUTE_SUB_ACCOUNTS = '/sub-accounts';
const CLASS_BORDER_ERROR = 'border-error ring-1 ring-error/20';
const CLASS_BORDER_DEFAULT = 'border-base-300';

const categoryConfig = [
  { key: 'workflows', label: 'Workflows', title: 'Workflows', empty: 'No workflows available' },
  { key: 'assignments', label: 'Assignments', title: 'Assignments', empty: 'No assignments available' },
];

const CloneSubAccount = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id: sourceId } = useParams();
  const { user } = useAuth();
  const [sourceSubAccount, setSourceSubAccount] = useState(location.state?.sub ?? null);
  const [parentAccounts, setParentAccounts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user?.scope !== 'platform') {
      navigate(ROUTE_SUB_ACCOUNTS, { replace: true });
    }
  }, [user, navigate]);
  const [submitting, setSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [isAccountOpen, setIsAccountOpen] = useState(false);
  const accountDropdownRef = useRef(null);
  const [form, setForm] = useState({
    account_id: user?.scope === 'account' ? user.accountUID : '',
    name: '',
    slug: '',
  });
  const [selectedCategories, setSelectedCategories] = useState({
    workflows: false,
    assignments: false,
  });
  const [subAccountBrandingOpen, setSubAccountBrandingOpen] = useState(false);
  const [workflowBrandingOpen, setWorkflowBrandingOpen] = useState(false);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (accountDropdownRef.current && !accountDropdownRef.current.contains(event.target)) {
        setIsAccountOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const loadAccounts = async () => {
      if (user?.scope === 'account') {
        setForm(f => ({ ...f, account_id: user.accountUID }));
        return;
      }
      try {
        const { data } = await accountService.getAccounts({ limit: 100 });
        setParentAccounts(data.data || data || []);
      } catch (err) {
        toastService.error('Failed to load accounts');
      }
    };
    loadAccounts();
  }, [user]);

  useEffect(() => {
    const loadSourceAndOptions = async () => {
      try {
        setLoading(true);
        let source = sourceSubAccount;

        if (!source?.accountId) {
          const { data } = await subAccountService.getSubAccounts(null, { limit: 100, page: 1 });
          source = (data.data || []).find(sub => sub.id === sourceId);
          if (!source) {
            throw new Error('Source sub-account not found');
          }
          setSourceSubAccount(source);
        }
      } catch (err) {
        toastService.error(err.message || 'Failed to load clone details');
        navigate(ROUTE_SUB_ACCOUNTS);
      } finally {
        setLoading(false);
      }
    };
    loadSourceAndOptions();
  }, [navigate, sourceId, sourceSubAccount]);



  const handleNameChange = (e) => {
    const name = e.target.value;
    const slug = name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
    setForm(f => ({ ...f, name, slug }));
    setFieldErrors(prev => ({ ...prev, name: null, slug: null }));
  };

  const setCategory = (key, checked) => {
    setSelectedCategories(prev => ({ ...prev, [key]: checked }));
  };

  const validateForm = () => {
    const errors = {};
    if (!form.account_id) { errors.account_id = 'Parent account is required'; }
    if (!form.name.trim()) { errors.name = 'Sub-account name is required'; }
    if (!form.slug.trim()) { errors.slug = 'Your domain is required'; }
    else if (!/^[a-z0-9-]+$/.test(form.slug)) { errors.slug = 'Your domain must only contain lowercase letters, numbers, and hyphens'; }
    const activeKeys = categoryConfig.map(c => c.key);
    const hasCategory = activeKeys.some(key => selectedCategories[key]);
    if (!hasCategory) { errors.categories = 'Please select at least one item to clone'; }
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) { return; }

    try {
      setSubmitting(true);
      await subAccountService.cloneSubAccount({
        sourceSubAccountId: sourceId,
        parentAccountId: form.account_id,
        name: form.name.trim(),
        slug: form.slug.trim(),
        categories: Object.entries(selectedCategories).filter(([, checked]) => checked).map(([key]) => key),
      });
      toastService.success('Sub-account cloned successfully');
      navigate(ROUTE_SUB_ACCOUNTS);
    } catch (err) {
      const msg = err.response?.data?.message;
      toastService.error(Array.isArray(msg) ? msg[0] : (msg || 'Failed to clone sub-account'));
    } finally {
      setSubmitting(false);
    }
  };

  const renderItemDetail = (key, item) => {
    if (key === 'assignments') {
      return `${item.workflowName || 'Workflow'} - ${item.status}`;
    }
    return item.industry || '';
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-32 space-y-4">
        <div className="w-12 h-12 border-4 border-primary/20 border-t-primary rounded-full animate-spin"></div>
        <p className="text-base-content/40 font-black uppercase tracking-widest text-xs">Loading Clone Details...</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-auto lg:h-[calc(100vh-3rem)] lg:overflow-hidden rounded-2xl border border-base-300 shadow-sm bg-base-100">
      <div className="flex-1 flex flex-col overflow-y-auto bg-base-100/50 backdrop-blur-md">
        <div className="p-6 sm:p-8 border-b border-base-300 flex-shrink-0 flex justify-between items-start">
          <div>
            <h1 className="page-title mb-1">Create Cloned Sub-Account</h1>
          </div>
          <button onClick={() => navigate(ROUTE_SUB_ACCOUNTS)} className="p-2 hover:bg-base-200 rounded-xl transition-colors text-base-content/40 hover:text-base-content">
            <X size={24} />
          </button>
        </div>

        <div className="p-6 sm:p-8 max-w-[1200px]">
          <form onSubmit={handleSubmit} className="space-y-10">
            <section className="space-y-3">
              <h2 className="text-[10px] font-black text-base-content/40 uppercase tracking-[0.2em]">Source Sub-Account</h2>
              <div className="rounded-xl border border-base-300 bg-base-content/5 px-4 py-3 flex items-center gap-3">
                <Layers size={18} className="text-primary shrink-0" />
                <div className="min-w-0">
                  <p className="font-black text-base-content truncate">{sourceSubAccount?.name}</p>
                  <p className="text-[10px] font-black text-base-content/30 uppercase tracking-widest">read-only</p>
                </div>
              </div>
            </section>

            <section className="space-y-6">
              <h2 className="text-[10px] font-black text-base-content/40 uppercase tracking-[0.2em]">New Sub-Account Details</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {user?.scope === 'platform' && (
                  <div className="space-y-2">
                    <label htmlFor="parentAccount" className="text-[10px] font-black text-base-content/40 uppercase tracking-[0.2em] block">
                      Parent Account *
                    </label>
                    <div className="relative" ref={accountDropdownRef}>
                      <button
                        id="parentAccount"
                        type="button"
                        onClick={() => setIsAccountOpen(!isAccountOpen)}
                        className={`flex items-center justify-between gap-2 w-full rounded-xl px-4 py-3 bg-base-100 border ${fieldErrors.account_id ? CLASS_BORDER_ERROR : CLASS_BORDER_DEFAULT} text-sm font-bold focus:ring-2 focus:ring-primary focus:border-primary outline-none transition-all group`}
                      >
                        <div className="flex items-center gap-2 truncate text-base-content">
                          <Building2 size={16} className="text-base-content/20 shrink-0" />
                          <span className="truncate">{parentAccounts.find(a => a.id === form.account_id)?.name || 'Select Parent Account'}</span>
                        </div>
                        <ChevronDown size={14} className={`text-base-content/20 group-hover:text-primary transition-transform shrink-0 ${isAccountOpen ? 'rotate-180' : ''}`} />
                      </button>
                      {isAccountOpen && (
                        <div className="absolute left-0 right-0 top-full mt-2 bg-base-100 border border-base-content/10 rounded-xl shadow-2xl z-[100] overflow-hidden animate-in fade-in slide-in-from-top-1 duration-200 flex flex-col max-h-60 overflow-y-auto">
                          <button
                            type="button"
                            onClick={() => {
                              setForm(f => ({ ...f, account_id: '' }));
                              setIsAccountOpen(false);
                            }}
                            className={`w-full px-4 py-3 text-left hover:bg-primary/5 transition-all flex items-center gap-2 group border-b border-base-content/5 ${!form.account_id ? 'bg-primary/5' : ''}`}
                          >
                            <span className={`text-[11px] font-black uppercase tracking-widest ${!form.account_id ? 'text-primary' : 'text-base-content/40 group-hover:text-primary'}`}>Select Parent Account</span>
                            {!form.account_id && <Check size={12} className="text-primary ml-auto" />}
                          </button>
                          {parentAccounts.map((acc) => (
                            <button
                              key={acc.id}
                              type="button"
                              disabled={acc.status === 'inactive'}
                              onClick={() => {
                                if (acc.status !== 'inactive') {
                                  setForm(f => ({ ...f, account_id: acc.id }));
                                  setIsAccountOpen(false);
                                  if (fieldErrors.account_id) setFieldErrors(prev => ({ ...prev, account_id: null }));
                                }
                              }}
                              className={`w-full px-4 py-3 text-left transition-all flex items-center gap-2 group border-b border-base-content/5 last:border-none ${acc.status === 'inactive'
                                ? 'cursor-not-allowed opacity-50 bg-base-content/5'
                                : form.account_id === acc.id
                                  ? 'bg-primary/5 hover:bg-primary/5'
                                  : 'hover:bg-primary/5'
                              }`}
                            >
                              <div className="flex-1">
                                <span className={`text-[11px] font-black uppercase tracking-widest ${acc.status === 'inactive'
                                  ? 'text-base-content/30'
                                  : form.account_id === acc.id
                                    ? 'text-primary'
                                    : 'text-base-content/40 group-hover:text-primary'
                                }`}>
                                  {acc.name}
                                </span>
                              </div>
                              {form.account_id === acc.id && acc.status !== 'inactive' && <Check size={12} className="text-primary ml-auto" />}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    {fieldErrors.account_id && <p className="text-error text-xs mt-1 font-medium">{fieldErrors.account_id}</p>}
                  </div>
                )}

                <div className="space-y-2">
                  <label htmlFor="name" className="text-[10px] font-black text-base-content/40 uppercase tracking-[0.2em] block">Sub-Account Name *</label>
                  <input
                    id="name"
                    type="text"
                    maxLength={50}
                    placeholder="e.g. Downtown Service Center"
                    className={`w-full px-4 py-3 bg-base-100 border rounded-xl text-base-content font-bold focus:ring-2 focus:ring-primary focus:border-primary outline-none transition-all ${fieldErrors.name ? CLASS_BORDER_ERROR : CLASS_BORDER_DEFAULT}`}
                    value={form.name}
                    onChange={handleNameChange}
                  />
                  {fieldErrors.name && <p className="text-error text-xs mt-1 font-medium">{fieldErrors.name}</p>}
                </div>

                {/* <div className="space-y-2">
                  <label htmlFor="slug" className="text-[10px] font-black text-base-content/40 uppercase tracking-[0.2em] block">Subdomain *</label>
                  <input
                    id="slug"
                    type="text"
                    maxLength={100}
                    placeholder="downtown-service-center"
                    className={`w-full px-4 py-3 bg-base-100 border rounded-xl text-base-content font-bold focus:ring-2 focus:ring-primary focus:border-primary outline-none transition-all ${fieldErrors.slug ? CLASS_BORDER_ERROR : CLASS_BORDER_DEFAULT}`}
                    value={form.slug}
                    onChange={e => {
                      const slug = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-').replace(/--+/g, '-');
                      setForm(f => ({ ...f, slug }));
                      setFieldErrors(prev => ({ ...prev, slug: null }));
                    }}
                  />
                  {fieldErrors.slug && <p className="text-error text-xs mt-1 font-medium">{fieldErrors.slug}</p>}
                </div> */}
              </div>
              {/* Subdomain */}
              <div className="grid grid-cols-2 gap-2 space-y-2">
                <div>
                  <label htmlFor="slug" className="text-[10px] font-black text-base-content/40 uppercase tracking-[0.2em] block">
                    Your domain *
                  </label>
                  <div className="relative">
                    <input
                      id="slug"
                      type="text"
                      maxLength={100}
                      placeholder="downtown-service-center"
                      className={`w-full px-4 py-3 bg-base-100 border rounded-xl text-base-content font-bold focus:ring-2 focus:ring-primary focus:border-primary outline-none transition-all ${fieldErrors.slug ? CLASS_BORDER_ERROR : CLASS_BORDER_DEFAULT}`}
                      value={form.slug}
                      onChange={e => {
                        const slug = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-').replace(/--+/g, '-');
                        setForm(f => ({ ...f, slug }));
                        setFieldErrors(prev => ({ ...prev, slug: null }));
                      }}
                    />
                  </div>
                </div>
                <span className="font-bold text-[15px] flex items-center pt-2">.{process.env.REACT_APP_BASE_URL_HOSTNAME}</span>
                {fieldErrors.slug && <p className="text-error text-xs font-medium !mt-0">{fieldErrors.slug}</p>}
              </div>
            </section>

            <section className="space-y-2">
              <h2 className="text-[10px] font-black text-base-content/40 uppercase tracking-[0.2em]">Data to Clone</h2>
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-5 items-start">
                {categoryConfig.map(({ key, label, icon: Icon, title, empty }) => (
                  <div key={key} className={`border rounded-xl bg-base-100 overflow-hidden ${fieldErrors.categories ? 'border-error' : 'border-base-300'}`}>
                    <label className="flex items-center gap-3 px-4 py-3 border-b border-base-300 cursor-pointer hover:bg-base-content/5 transition-colors">
                      <input
                        type="checkbox"
                        className="checkbox checkbox-primary checkbox-sm"
                        checked={selectedCategories[key]}
                        onChange={e => { setCategory(key, e.target.checked); setFieldErrors(prev => ({ ...prev, categories: null })); }}
                      />
                      <span className="font-black text-base-content">{label}</span>
                    </label>
                    {key === 'assignments' && selectedCategories.assignments && (
                      <div className="flex items-start gap-2 px-4 py-2.5 bg-warning/10 text-warning-content">
                        <Info size={14} className="text-warning shrink-0 mt-0.5" />
                        <p className="text-[11px] font-medium text-warning">
                          Assignments with <span className="font-black">In Progress</span> status will be cloned as <span className="font-black">Expired</span> in the new sub-account.
                        </p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
              {fieldErrors.categories && <p className="text-error text-xs mt-1 font-medium">{fieldErrors.categories}</p>}
            </section>

            {/* ── Branding Fields (read-only, copied from source) ──────────── */}
            <section className="space-y-3">
              <div className="flex items-center gap-2">
                <h2 className="text-[10px] font-black text-base-content/40 uppercase tracking-[0.2em]">Branding</h2>
              </div>

              {/* Sub-Account Branding accordion */}
              <div className="border border-base-300 rounded-xl overflow-hidden opacity-70">
                <button
                  type="button"
                  onClick={() => setSubAccountBrandingOpen(o => !o)}
                  className="w-full flex items-center justify-between px-5 py-4 bg-base-100 hover:bg-base-200 transition"
                >
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 bg-primary/10 rounded-lg flex items-center justify-center">
                      <Palette size={14} className="text-primary" />
                    </div>
                    <div className="text-left">
                      <p className="text-sm font-semibold text-base-content">Sub-Account Branding</p>
                      <p className="text-xs text-base-content/50">Theme, logo and favicon for this sub-account</p>
                    </div>
                  </div>
                  <ChevronRight size={16} className={`text-base-content/30 transition-transform ${subAccountBrandingOpen ? 'rotate-90' : ''}`} />
                </button>

                {subAccountBrandingOpen && (
                  <div className="px-5 pb-5 pt-5 border-t border-base-300 space-y-5 bg-base-200/50">
                    {/* Experience Theme */}
                    <div>
                      <label className="text-[10px] font-black text-base-content/40 uppercase tracking-[0.2em] block mb-2">Experience Theme</label>
                      <div className="flex items-center gap-2 flex-wrap">
                        {DAISY_THEMES.map((theme) => {
                          const isActive = (sourceSubAccount?.theme || 'light') === theme;
                          return (
                            <div
                              key={theme}
                              className={`relative flex items-center gap-2 p-1.5 rounded-xl select-none border-2 cursor-not-allowed ${
                                isActive ? 'border-primary bg-primary/10 shadow-sm' : 'border-base-300 bg-base-100'
                              }`}
                            >
                              <div data-theme={theme} className="bg-base-100 grid grid-cols-2 gap-0.5 rounded-lg p-1 shadow-sm border border-base-content/5 w-7 h-7 shrink-0 place-items-center relative">
                                <div className="bg-base-content w-1 h-1 rounded-full" />
                                <div className="bg-primary w-1 h-1 rounded-full" />
                                <div className="bg-secondary w-1 h-1 rounded-full" />
                                <div className="bg-accent w-1 h-1 rounded-full" />
                                {isActive && (<div className="absolute inset-0 bg-primary/10 rounded-lg flex items-center justify-center"><svg className="w-3.5 h-3.5 text-primary drop-shadow-sm" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="4"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg></div>)}
                              </div>
                              <span className={`text-[11px] font-bold capitalize truncate leading-none pr-1 ${isActive ? 'text-primary' : 'text-base-content/70'}`}>{theme}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Brand Logo URL */}
                    <div className="mt-4">
                      <label className="text-[10px] font-black text-base-content/40 uppercase tracking-[0.2em] block mb-2">Brand Logo URL</label>
                      {sourceSubAccount?.logoUrl && (
                        <div className="mt-2 flex flex-col gap-1">
                          <img src={sourceSubAccount.logoUrl} alt="Logo preview" className="h-[100px] w-[100px] object-contain rounded border border-base-300 bg-base-100 p-0.5" />
                        </div>
                      )}
                    </div>

                    {/* Favicon URL */}
                    <div>
                      <label className="text-[10px] font-black text-base-content/40 uppercase tracking-[0.2em] block mb-2">Favicon URL</label>
                      {sourceSubAccount?.faviconUrl && (
                        <div className="mt-2 flex flex-col gap-1">
                          <img src={sourceSubAccount.faviconUrl} alt="Favicon preview" className="h-[100px] w-[100px] object-contain rounded border border-base-300 bg-base-100 p-0.5" />
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Workflow Branding accordion */}
              <div className="border border-base-300 rounded-xl overflow-hidden opacity-70">
                <button
                  type="button"
                  onClick={() => setWorkflowBrandingOpen(o => !o)}
                  className="w-full flex items-center justify-between px-5 py-4 bg-base-100 hover:bg-base-200 transition"
                >
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 bg-primary/10 rounded-lg flex items-center justify-center">
                      <Palette size={14} className="text-primary" />
                    </div>
                    <div className="text-left">
                      <p className="text-sm font-semibold text-base-content">Workflow Branding</p>
                      <p className="text-xs text-base-content/50">Default theme and logo for workflows under this sub-account</p>
                    </div>
                  </div>
                  <ChevronRight size={16} className={`text-base-content/30 transition-transform ${workflowBrandingOpen ? 'rotate-90' : ''}`} />
                </button>

                {workflowBrandingOpen && (
                  <div className="px-5 pb-5 pt-5 border-t border-base-300 space-y-5 bg-base-200/50">
                    {/* Workflow Theme */}
                    <div>
                      <label className="text-[10px] font-black text-base-content/40 uppercase tracking-[0.2em] block mb-2">Workflow Theme</label>
                      <p className="text-base-content/40 text-[10px] font-medium mb-2">Default theme applied when creating workflows under this sub-account.</p>
                      <div className="flex items-center gap-2 flex-wrap">
                        {DAISY_THEMES.map((theme) => {
                          const isActive = (sourceSubAccount?.workflowTheme || 'light') === theme;
                          return (
                            <div
                              key={theme}
                              className={`relative flex items-center gap-2 p-1.5 rounded-xl select-none border-2 cursor-not-allowed ${
                                isActive ? 'border-primary bg-primary/10 shadow-sm' : 'border-base-300 bg-base-100'
                              }`}
                            >
                              <div data-theme={theme} className="bg-base-100 grid grid-cols-2 gap-0.5 rounded-lg p-1 shadow-sm border border-base-content/5 w-7 h-7 shrink-0 place-items-center relative">
                                <div className="bg-base-content w-1 h-1 rounded-full" />
                                <div className="bg-primary w-1 h-1 rounded-full" />
                                <div className="bg-secondary w-1 h-1 rounded-full" />
                                <div className="bg-accent w-1 h-1 rounded-full" />
                                {isActive && (<div className="absolute inset-0 bg-primary/10 rounded-lg flex items-center justify-center"><svg className="w-3.5 h-3.5 text-primary drop-shadow-sm" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="4"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg></div>)}
                              </div>
                              <span className={`text-[11px] font-bold capitalize truncate leading-none pr-1 ${isActive ? 'text-primary' : 'text-base-content/70'}`}>{theme}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Workflow Logo URL */}
                    <div>
                      <label className="text-[10px] font-black text-base-content/40 uppercase tracking-[0.2em] block mb-2">Workflow Logo URL</label>
                      <p className="text-base-content/40 text-[10px] font-medium mb-2">Default logo shown on workflow capture screens for this sub-account.</p>
                      {sourceSubAccount?.workflowLogoUrl && (
                        <div className="mt-2 flex flex-col gap-1">
                          <img src={sourceSubAccount.workflowLogoUrl} alt="Workflow logo preview" className="h-[100px] w-[100px] object-contain rounded border border-base-300 bg-base-100 p-0.5" />
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Enable QR Code Access toggle — disabled */}
              <div className="flex items-center justify-between p-4 bg-base-100 border border-base-300 rounded-xl opacity-70 cursor-not-allowed">
                <div>
                  <span className="block text-sm font-bold text-base-content">Enable QR Code Access</span>
                  <span className="block text-xs text-base-content/50 mt-0.5">Show QR codes for workflow assignments created under this sub-account.</span>
                </div>
                <label className="relative inline-flex items-center cursor-not-allowed">
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={sourceSubAccount?.showQrCode ?? true}
                    disabled
                    readOnly
                  />
                  <div className="w-11 h-6 bg-base-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                </label>
              </div>
            </section>

            <div className="flex gap-4 pt-12 border-t border-base-content/5">
              <button
                type="button"
                onClick={() => navigate(ROUTE_SUB_ACCOUNTS)}
                className="flex-1 py-4 px-8 border border-base-content/20 rounded-xl font-black uppercase tracking-[0.2em] text-xs text-base-content/40 hover:bg-base-content/5 hover:text-base-content transition-all"
              >
                Cancel
              </button>
              <button type="submit" disabled={submitting} className="flex-1 btn-premium">
                {submitting ? (
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin"></div>
                    <span>Cloning...</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <Copy size={18} />
                    <span>Clone Sub-Account</span>
                  </div>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CloneSubAccount;
